/**
 * UNE cause par compteur, la plus précise (DEV-1619).
 *
 * ## Le défaut que ce helper ferme
 *
 * La page d'un compteur portait DEUX surfaces qui parlent de la même chose sans
 * se connaître :
 *
 * 1. la bannière de workflow Enedis (`useWorkflowEnedisMessage`), servie par le
 *    verdict d'api-metier — elle nomme la cause avec le vocabulaire du verdict
 *    (`historique_pdl_hors_service`, `historique_identifiant_inconnu`, …) ;
 * 2. un message global historique, écrit avant que la couche verdict existe, qui
 *    déduisait la cause d'un seul champ dapi, `informations.pdl.etat`.
 *
 * Le 04/09/2026 le compteur `01114905808921` (RAMERY ENERGIES) lisait les deux à
 * la fois, et elles se contredisaient : « résilié ou hors service auprès
 * d'Enedis » en haut, puis « n'existe pas sur le réseau » avec un numéro de
 * dépannage en bas du bloc Graphiques. Faits servis le même jour :
 * `informations.pdl.etat = "resilie"` et
 * `data_retrieval.status.cause = "pdl_hors_service"` (dapi prod, lecture
 * 2026-09-04) ; verdict enedis `existe: true`,
 * `identifiant_inconnu_instruit: false` (lecture 2026-09-04T11:18Z). Le second
 * message était donc faux d'après nos propres faits.
 *
 * ## La règle
 *
 * - « n'existe pas sur le réseau » ne se sert QUE si le point est réellement
 *   inconnu du réseau, soit `etat === "inexistant"` — la seule des trois valeurs
 *   de `EtatPdl` (`en_service | resilie | inexistant`, `dapi`
 *   `get_information_source_model/output_model_pdl.py`) qui soutienne la phrase.
 *   `resilie` a sa propre cause, `pdl_hors_service`, et sa propre copie.
 * - Le numéro de dépannage part avec la phrase : c'est un canal réseau, pas un
 *   canal de télé-relève, et il n'a rien à faire chez un compteur qui existe.
 * - Et, plus général que le cas : dès qu'une cause est DÉJÀ nommée par le
 *   verdict, cette surface historique se tait. Le verdict lit tout le substrat
 *   (contrat, consentement, absences instruites, souscription) là où ce message
 *   ne lit qu'un champ ; deux causes affichées, c'est au mieux une redite, au
 *   pire la contradiction du 04/09. Un seul champ ne peut pas contredire le
 *   juge.
 *
 * Écrit en helper pur, hors du composant, pour être testable sans monter la page.
 */

/**
 * État contractuel du point servi par dapi (`informations.pdl.etat`).
 * Miroir de l'énum `EtatPdl` côté dapi — trois valeurs, pas deux.
 */
export const ETAT_PDL_EN_SERVICE = "en_service"
export const ETAT_PDL_RESILIE = "resilie"
export const ETAT_PDL_INEXISTANT = "inexistant"

/**
 * Le message « le compteur n'existe pas sur le réseau » peut-il être servi ?
 *
 * @param etatPdl - `informations.pdl.etat` servi par dapi, tel quel. `null` ou
 *   inconnu ⇒ on ne dit rien : aucune affirmation sans le fait qui la porte.
 * @param aUneDemandeDeConsentement - `source.consentrequest` : une demande de
 *   consentement en attente a sa propre copie, prioritaire (comportement
 *   inchangé).
 * @param uneCauseEstDejaNommee - le verdict Enedis sert-il déjà au moins une
 *   bannière sur cet écran ? Si oui, cette surface se tait.
 * @returns `true` seulement si le point est déclaré inexistant ET qu'aucune
 *   autre cause n'est servie.
 */
export const sertLaCauseCompteurInexistant = ({
    etatPdl,
    aUneDemandeDeConsentement,
    uneCauseEstDejaNommee,
}: {
    etatPdl: string | null | undefined
    aUneDemandeDeConsentement: boolean
    uneCauseEstDejaNommee: boolean
}): boolean => {
    if (aUneDemandeDeConsentement) return false
    if (uneCauseEstDejaNommee) return false
    return etatPdl === ETAT_PDL_INEXISTANT
}
